import { Injectable , inject} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { AuthRepository } from 'app/core/auth/auth.repository';
import { AppConfigService } from 'app/core/config/app-config.service';

// ─── Types ─────────────────────────────────────────────

export interface StudentPointsConfig {
  course_start:           number;
  course_finish:          number;
  clear_word:             number;
  read_text:              number;
  read_pdf:               number;
  read_pdf_per_page:      number;
  watch_video:            number;
  watch_video_per_minute: number;
  gallery_per_slide:      number;
  write_essay:            number;
  drill_yourself:         number;
  drill_comp:             number;
  quiz:                   number;
  final_test:             number;
}

export interface AccountPolicySettings {
  never_logged_in_days: number | null;
}

/**
 * Time-limited course access. Resolved student → course → company → system,
 * where null means "inherit from the next layer up". The system layer is the
 * only one whose values are always concrete, so resolution terminates there.
 */
export interface CourseAccessSettings {
  allow_access_after_completion?: boolean | null;
  access_window: {
    enabled:     boolean | null;
    days:        number | null;
    /** enrollment | start | first_open */
    anchor:      string | null;
    /** Absolute Y-m-d; wins over days/anchor when set. */
    valid_until: string | null;
  };
  repeat: {
    allowed: boolean | null;
  };
}

export interface SystemSettings {
  featuresTopNav: boolean;
  /** Language codes companies may offer; managed in the Languages tab. */
  system_languages?: string[];
  feature_firststart: boolean;
  feature_classroom: boolean;
  feature_clearword: boolean;
  feature_chat_supervisor: boolean;
  feature_chat_twin: boolean;
  feature_progress_rocket: boolean;
  feature_dashboard_courses: boolean;
  feature_dashboard_available_student: boolean;
  feature_dashboard_available_supervisor: boolean;
  feature_dashboard_available_courseadmin: boolean;
  feature_badges: boolean;
  feature_ai: boolean;
  /** AI must reach its flow terminal before a step can be completed. Default off. */
  feature_ai_step_gate: boolean;
  /** Enable the Word Clarification course wizard. */
  feature_word_clarification?: boolean;
  /** Enable the guided AI Course Wizard. */
  feature_ai_course_wizard?: boolean;
  feature_supervisor: boolean;
  feature_twin: boolean;
  feature_orientation_tour: boolean;
  feature_cross_company: boolean;
  feature_checksheet_collapsed: boolean;
  feature_checksheet_hidden: boolean;
  feature_checksheet_progress_bar: boolean;
  feature_checksheet_full_color: boolean;
  feature_show_step_messages: boolean;
  feature_thumbs_up_buttons: boolean;
  feature_show_step_number: boolean;
  feature_hide_editor_toolbar: boolean;
  feature_voice_input: boolean;
  feature_input_selector_images: boolean;
  feature_study_matrix: boolean;
  feature_student_groups: boolean;
  feature_reports: boolean;
  /** Awards feature for companies */
  feature_awards?: boolean;
  /** D5 performance rating for students */
  feature_d5_rating?: boolean;
  /** Study plan / learning path management */
  feature_study_plans?: boolean;
  /**
   * Show the jump-outs into the legacy PHP course editor.
   *
   * The Angular editor covers what `course-editor.php` did, so this exists to switch the
   * old system off per deployment once its courses have been checked over — and to
   * switch it back on if something is missing, without a rebuild. Default off: a
   * deployment that still needs it says so.
   */
  feature_legacy_course_editor?: boolean;
  /** Subtitle Editor button on video steps in the course editor (kopilot whisper service) */
  feature_subtitle_editor?: boolean;
  /** Global e-commerce switch: a company's Website Sales only counts while this is on. */
  feature_website_sales?: boolean;
  /** Show/hide the Help / FAQ button in the top navigation bar */
  feature_help: boolean;
  /** Bottom of the course-access chain, so these are concrete rather than null. */
  course_access?: CourseAccessSettings;
  /** Default AI depth for a course left on inherit. off | simple | extended. */
  ai_mode?: 'off' | 'simple' | 'extended';
  /** Show "I have a win" for students in the top navigation bar */
  feature_ihaveawin: boolean;
  /** Show the Game Zone button in the top header */
  feature_game: boolean;
  /** Show/hide the "publish results" toggle on the course Completion step */
  feature_completion_publish: boolean;
  /** Email sending mode — editable by SuperUser/SysAdmin only.
   *  'normal'   → send to real recipients
   *  'redirect' → redirect all email to email_debug_test_email
   *  'stop'     → suppress all outgoing email
   *  Source of truth; the legacy email_debug_* flags are kept in sync by the backend. */
  email_mode?: 'normal' | 'redirect' | 'stop';
  /** Email debug / test-mode — editable by SuperUser/SysAdmin only (legacy, derived from email_mode) */
  email_debug_enabled?: boolean;
  email_debug_test_email?: string;
  email_debug_redirect_all?: boolean;
  email_debug_log_recipients?: boolean;
  /** System-wide default point values awarded on step completion */
  student_points?: StudentPointsConfig;
  /** Days an unused account remains Registered New before becoming Never Logged In. */
  account_policy?: AccountPolicySettings;
  [key: string]: any;
}

export interface CompanySettings {
  branding: {
    brand_color_1:    string;
    brand_color_2:    string;
    brand_color_3:    string;
    brand_logo:       string;
    brand_logo_light: string;
    brand_logo_dark:  string;
    login_background: string;
    favicon?:         string;  // media_id — browser tab favicon (shown after login)
    white_navigation: boolean;
    custom_css?:      string;  // freeform CSS injected into step views for this company
    win_header?:      string;  // media_id or URL — optional header image for the "I Have a Win" dialog
    content_header?:  string;  // media_id — banner image on dashboard and profile pages (160px strip)
    certificate_badge?: string; // media_id — honor badge shown on completed course cards
    footer_text?:     string;  // email footer text shown at the bottom of outgoing emails
  };
  languages: {
    available: string[];
    default: string;
  };
  /** Per-company feature flag overrides. null = inherit from system. */
  features: {
    [key: string]: boolean | null;
  };
  onboarding: {
    page_welcome: boolean;
    page_culture_settings: boolean;
    page_student_data_form: boolean;
    page_check_phone: boolean;
    page_check_creditcard: boolean;
    page_check_email: boolean;
    page_terms_and_conditions: boolean;
    page_check_audio_video: boolean;
    page_profile_image: boolean;
    page_scheduler: boolean;
    page_course_info: boolean;
    page_clear_word_intro: boolean;
    page_start: boolean;
    [key: string]: boolean;
  };
  welcome_page: {
    title:        string;
    subtitle:     string;
    description?: string;
    hero_image:   string;
  };
  validation: {
    validate_phone: boolean;
    validate_creditcard: boolean;
    validate_email: boolean;
    confirm_message_send?: boolean;
  };
  notifications?: {
    enabled: boolean;
    /** Delivery channels. in_app is always used for student help requests. */
    channels?: {
      in_app?: boolean;
      email?: boolean;
      push?: boolean;
    };
    /** Email every in-app message to supervisors, overriding event_channels for email. */
    email_all_in_app?: boolean;
    /** Per-event channel overrides. Key "prefix.*" matches a whole group. */
    event_channels?: {
      [eventKey: string]: { email?: boolean; push?: boolean };
    };
    events?: {
      student_submission?: boolean;
      supervisor_approval?: boolean;
      supervisor_rejection?: boolean;
      student_resubmission?: boolean;
    };
    step_types?: {
      watch_video?: boolean;
      read_text?: boolean;
      read_pdf?: boolean;
      write_essay?: boolean;
      do_drill?: boolean;
      checkout?: boolean;
      course_start?: boolean;
      completion?: boolean;
      drill_skype?: boolean;
      glossary?: boolean;
      policy?: boolean;
      youtube?: boolean;
      quiz?: boolean;
      final_test?: boolean;
      how_are_you?: boolean;
      twinning?: boolean;
      supervisor?: boolean;
      gallery?: boolean;
      badge?: boolean;
      video_slides?: boolean;
    };
  };
  terms_and_conditions: {
    current_version:    string;
    versions: Array<{
      version:    string;
      content:    Record<string, string>; // lang → HTML
      created_at: string;
    }>;
    terms_enabled?:     boolean;
    terms_pdf_media_id?: string;
    gdpr_enabled?:      boolean;
    gdpr_content?:      Record<string, string> | string;
    gdpr_pdf_media_id?: string;
  };
  /** Per-company point overrides. null = inherit from system default. */
  student_points?: { [K in keyof StudentPointsConfig]: number | null };
  /** Per-company account policy overrides. null = inherit from system. */
  account_policy?: AccountPolicySettings;
  /** Per-company course access overrides. null = inherit from system. */
  course_access?: CourseAccessSettings;
  /** Default AI depth for this company's courses. null = inherit the system mode. */
  ai_mode?: 'off' | 'simple' | 'extended' | null;
  copyright: string;
  /**
   * Picture and clip generation in the course wizard. Off until a company opts in.
   * A monthly limit of 0 keeps that kind off; below 0 means no monthly cap.
   */
  media_generation?: {
    enabled: boolean;
    monthly_images: number;
    monthly_videos: number;
  };
  ai_image_style?: {
    style_prompt: string;
    reference_images: string[];
  };
  contact_email: string;
  contact_name: string;
  company_info: {
    name: string;
    email: string;
    phone: string;
    address: string;
    payment_info: string;
  };
  session?: {
    inactivity_timeout_minutes?: number; // 0 = disabled; default 15
    vc_track_idle_minutes?: number;       // LiveKit track-pause threshold; 0 = disabled; default 5
  };
  messaging?: {
    ack_required?: boolean; // Students must click OK to acknowledge supervisor/twin messages (default: true)
  };
  /** Website Sales (WooCommerce / Square) integration — per-company opt-in. */
  website_sales?: {
    enabled: boolean;
    /** Always blank on read — the API key is write-protected/masked. */
    webhook_secret?: string;
    /** True when a key has been generated for this company. */
    webhook_secret_set?: boolean;
    /** Last 4 chars of the stored key, for display only. */
    webhook_secret_last4?: string;
    send_welcome_email_default: boolean;
    /** Square shop for the static website. Token and signature key are write-only (blank on read). */
    square?: {
      enabled: boolean;
      environment: 'sandbox' | 'production';
      application_id: string;
      location_id: string;
      access_token?: string;
      access_token_set?: boolean;
      webhook_signature_key?: string;
      webhook_signature_key_set?: boolean;
      webhook_url: string;
      site_url: string;
      currency: string;
      gift_card_amounts: number[];
    };
    /** Which provider the website checkout uses. */
    provider?: 'square' | 'stripe';
    /** Stripe shop for the static website. Secret key and webhook secret are write-only (blank on read). */
    stripe?: {
      enabled: boolean;
      publishable_key: string;
      secret_key?: string;
      secret_key_set?: boolean;
      webhook_secret?: string;
      webhook_secret_set?: boolean;
      webhook_url: string;
      site_url: string;
      currency: string;
      gift_card_amounts: number[];
    };
    /** The legal entity that sells; printed on shop invoices and credit notes. */
    seller?: {
      legal_name: string;
      address: string;
      country: string;
      email: string;
      vat_id: string;
      tax_number: string;
    };
    /** Invoice numbering and presentation (seller_* are the older seller fields). */
    invoice?: {
      prefix: string;
      seller_name: string;
      seller_address: string;
      seller_email: string;
      vat_id: string;
      vat_rate: number;
      small_business: boolean;
      footer_note: string;
    };
    /**
     * VAT handling. An empty mode follows invoice.small_business (exempt, otherwise vat).
     * per_country pricing applies to Stripe only. rates overrides the built-in EU rates (percent).
     */
    tax?: {
      mode: '' | 'exempt' | 'vat' | 'vat_oss';
      pricing: 'uniform_gross' | 'per_country';
      rates: Record<string, number>;
      exempt_note: string;
      accounts: Record<string, string>;
      codes: Record<string, string>;
      datev: { consultant: string; client: string };
    };
  };
  /**
   * WordPress knowledge bases behind the glossary and policy step types, and the
   * target for glossary entries published by the word wizard.
   *
   * The flat url/username/password is the default store and predates `stores`, which
   * is why it stays at this level: every existing install has it, and the API exposes
   * it under the reserved store id `default`.
   */
  kdb?: KdbSettings;
  [key: string]: any;
}

export interface KdbSettings {
  url: string;
  username: string;
  /** Always blank on read — the password is write-protected/masked. */
  password?: string;
  /** True when a password has been stored for this store. */
  password_set?: boolean;
  /** Whether this company may write entries, not just read them. */
  publish_enabled?: boolean;
  /**
   * Which store a request that names none lands on. `default` is the flat block above.
   * Naming a store that is not usable falls back rather than leaving none.
   */
  default_store?: string;
  stores?: KdbStoreSettings[];
}

/** One additional knowledge base beyond the default. */
export interface KdbStoreSettings {
  /** Lowercase `[a-z0-9_-]`; `default` is reserved for the flat block above. */
  id: string;
  label: string;
  url: string;
  username: string;
  password?: string;
  password_set?: boolean;
}

/**
 * Onboarding for SysAdmins, Translators and Supervisors that belong to no company.
 * Content is shared; role_pages holds each role's wizard pages ('2', '7', '11').
 */
export interface StaffOnboardingSettings {
  branding: { brand_logo: string; brand_color_1: string };
  languages: { available: string[]; default: string };
  welcome_page: CompanySettings['welcome_page'];
  validation: { validate_phone: boolean; validate_creditcard: boolean; validate_email: boolean };
  terms_and_conditions: CompanySettings['terms_and_conditions'];
  role_pages: Record<string, Record<string, boolean>>;
}

/** Login page and email branding used when a company has none. Media values are company-media ids. */
export interface SystemBrandingSettings {
  login: { logo: string; background: string };
  email: { logo: string; header_color: string; footer_text: string };
}

export interface StaffOnboardingResponse {
  data: StaffOnboardingSettings;
  /** role => pages that role can have, in wizard order */
  pages: Record<string, string[]>;
  system_languages: string[];
}

/** Response shape from GET /company-settings/{id} */
export interface CompanySettingsResponse {
  data: CompanySettings;
  editable_keys: string[];
  system_features: SystemSettings;
  system_languages: string[];
  /** Studema UI translation progress per system language. */
  language_status: Record<string, { translated: number; total: number; percent: number }>;
  company_id: number;
}

// Simple XOR-based obfuscation for localStorage (not cryptographic, but prevents plain-text reading)
const STORAGE_KEY_SYS = 'st_sys_cfg';
const STORAGE_KEY_COMPANY = 'st_cmp_cfg';
const OBFUSCATION_KEY = 'sTuDeMa2026!';

function obfuscate(data: string): string {
  let result = '';
  for (let i = 0; i < data.length; i++) {
    result += String.fromCharCode(
      data.charCodeAt(i) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length)
    );
  }
  return btoa(result);
}

function deobfuscate(encoded: string): string {
  try {
    const data = atob(encoded);
    let result = '';
    for (let i = 0; i < data.length; i++) {
      result += String.fromCharCode(
        data.charCodeAt(i) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length)
      );
    }
    return result;
  } catch {
    return '{}';
  }
}

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

/**
 * Keys that are subject to the client-config licence ceiling. Branding, URLs and
 * other non-flag settings keep their plain config.json → environment cascade.
 */
export function isFeatureFlagKey(key: string): boolean {
  return key.startsWith('feature_') || key === 'featuresTopNav';
}

// ─── Service ───────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class FeatureSettingsService {
  private get baseUrl(): string { return this.appConfig.get('api_baseurl'); }

  private _systemSettings$ = new BehaviorSubject<SystemSettings | null>(null);
  private _companySettings$ = new BehaviorSubject<CompanySettings | null>(null);

  /** Observable of current system settings */
  systemSettings$ = this._systemSettings$.asObservable();
  /** Observable of current company settings */
  companySettings$ = this._companySettings$.asObservable();

  constructor(
    private http: HttpClient,
    private authRepository: AuthRepository,
    readonly appConfig: AppConfigService,
  ) {
    // Try to restore from localStorage on startup
    this.restoreFromStorage();
  }

  // ═══════════════════════════════════════════════════════
  //  INITIALIZATION (called after login)
  // ═══════════════════════════════════════════════════════

  /**
   * Called from the login flow to store settings received in the login response.
   */
  initFromLoginResponse(loginData: any): void {
    if (loginData.system_settings) {
      this._systemSettings$.next(loginData.system_settings as SystemSettings);
      this.saveToStorage(STORAGE_KEY_SYS, loginData.system_settings);
    }
    if (loginData.company_settings) {
      this._companySettings$.next(loginData.company_settings as CompanySettings);
      this.saveToStorage(STORAGE_KEY_COMPANY, loginData.company_settings);
      this.applyFaviconBranding(loginData.company_settings as CompanySettings);
      this.applyCustomStepCss(loginData.company_settings as CompanySettings);
    }
  }

  /**
   * Restore settings from encrypted localStorage.
   */
  private restoreFromStorage(): void {
    try {
      const sysRaw = localStorage.getItem(STORAGE_KEY_SYS);
      if (sysRaw) {
        const parsed = JSON.parse(deobfuscate(sysRaw));
        this._systemSettings$.next(parsed);
      }
    } catch {
      /* ignore */
    }

    try {
      const cmpRaw = localStorage.getItem(STORAGE_KEY_COMPANY);
      if (cmpRaw) {
        const parsed = JSON.parse(deobfuscate(cmpRaw));
        this._companySettings$.next(parsed);
        this.applyFaviconBranding(parsed);
        this.applyCustomStepCss(parsed);
      }
    } catch {
      /* ignore */
    }
  }

  private saveToStorage(key: string, data: any): void {
    try {
      localStorage.setItem(key, obfuscate(JSON.stringify(data)));
    } catch {
      /* ignore */
    }
  }

  /**
   * Clear all stored settings (on logout).
   */
  clearSettings(): void {
    this._systemSettings$.next(null);
    this._companySettings$.next(null);
    localStorage.removeItem(STORAGE_KEY_SYS);
    localStorage.removeItem(STORAGE_KEY_COMPANY);
    this.applyFaviconBranding(null);
    this.applyCustomStepCss(null);
  }

  /**
   * Dynamically updates the browser-tab favicon to the company-specific one after login,
   * and restores the default static favicon on logout or when no favicon is configured.
   */
  private applyFaviconBranding(settings: CompanySettings | null): void {
    try {
      const faviconId = settings?.branding?.favicon;
      const links = Array.from(document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']"));
      if (faviconId) {
        const token = this.authRepository.getAccessToken();
        const url   = `${this.appConfig.get('serverUrl')}/v1/company-media/${faviconId}?token=${token}`;
        links.forEach(link => { link.href = url; });
        if (links.length === 0) {
          const link = document.createElement('link');
          link.rel  = 'icon';
          link.type = 'image/png';
          link.href = url;
          document.head.appendChild(link);
        }
      } else {
        // Restore static defaults
        links.forEach((link, i) => {
          link.href = i === 0 ? 'favicon-16x16.png' : 'favicon-32x32.png';
        });
      }
    } catch {
      /* ignore — DOM access may not be available in SSR or test environments */
    }
  }

  /**
   * Injects (or removes) a <style> tag with the company's custom step CSS.
   * The tag is scoped under .checksheet-step-page to prevent unintended side effects.
   * Call with null to remove the tag (logout / company switch).
   */
  applyCustomStepCss(settings: CompanySettings | null): void {
    try {
      const STYLE_ID = 'checksheet-company-custom-css';
      const existing = document.getElementById(STYLE_ID);
      const css = settings?.branding?.custom_css?.trim() || '';
      if (!css) {
        existing?.remove();
        return;
      }
      const tag = existing || document.createElement('style');
      tag.id = STYLE_ID;
      tag.textContent = css;
      if (!existing) {
        document.head.appendChild(tag);
      }
    } catch {
      /* DOM may be unavailable in SSR or test environments */
    }
  }

  // ═══════════════════════════════════════════════════════
  //  FEATURE FLAG GETTERS
  //  Uses: DB settings > environment.ts fallback
  // ═══════════════════════════════════════════════════════

  /**
   * True when the client-config licenses this feature flag.
   *
   * /_config/config.json is the per-installation licence file. An explicit
   * `false` there is a hard ceiling: no DB setting and no admin UI can lift it.
   * A key that is absent counts as licensed, so flags added in a later build
   * stay usable until ops writes `false` into that server's config.json.
   */
  isLicensed(key: string): boolean {
    if (!isFeatureFlagKey(key)) { return true; }
    return this.appConfig.get<boolean>(key, true) !== false;
  }

  /**
   * Get a feature flag value.
   * Resolution order: client-config licence ceiling → company-level override →
   * system-level (merged with config defaults) → config fallback.
   * A company override of `null` means "inherit from system".
   */
  getFeature(key: string): any {
    // 0. Client-config licence ceiling — an explicit false in config.json wins over everything.
    if (!this.isLicensed(key)) {
      return false;
    }

    const globallyCapped =
      key === 'feature_word_clarification' || key === 'feature_ai_course_wizard';
    if (globallyCapped && !(this.getSystemSettings() as any)[key]) {
      return false;
    }

    // 1. Company-level override (null = not set = inherit from system)
    const company = this._companySettings$.getValue();
    const companyOverride = company?.features?.[key];
    if (companyOverride !== undefined && companyOverride !== null) {
      return companyOverride;
    }

    // 2. System-level setting merged with config.json defaults.
    // getSystemSettings() returns { ...configDefaults, ...(rawSystemSettings || {}) } so that
    // flags not yet saved to the DB still resolve to their config.json / hard-coded defaults.
    const merged = this.getSystemSettings();
    if (merged && key in merged) {
      return (merged as any)[key];
    }

    // 3. Config / environment fallback (for non-system keys like serverUrl, branding, etc.)
    return this.appConfig.get(key);
  }

  /** Shorthand boolean check */
  isFeatureEnabled(key: string): boolean {
    return !!this.getFeature(key);
  }

  /**
   * Get the VC track-idle timeout (minutes) from company settings.
   * Falls back to 5 minutes. 0 = disabled.
   */
  getVcTrackIdleMinutes(): number {
    const company = this._companySettings$.getValue();
    const configured = (company?.session as any)?.vc_track_idle_minutes;
    return configured !== undefined && configured !== null ? (configured as number) : 5;
  }

  /**
   * Get all current system settings (merged with config.json defaults).
   * Feature flags are clamped by the client-config licence ceiling, so callers
   * that read a flag straight off this object cannot see an unlicensed `true`.
   */
  getSystemSettings(): SystemSettings {
    const sys = this._systemSettings$.getValue();
    const cfg = this.appConfig;
    const configDefaults: Partial<SystemSettings> = {
      // General / navigation
      featuresTopNav:              cfg.get('featuresTopNav',              true),
      feature_firststart:          cfg.get('feature_firststart',          false),
      feature_classroom:           cfg.get('feature_classroom',           false),
      feature_clearword:           cfg.get('feature_clearword',           false),
      feature_chat_supervisor:     cfg.get('feature_chat_supervisor',     false),
      feature_chat_twin:           cfg.get('feature_chat_twin',           false),
      feature_progress_rocket:     cfg.get('feature_progress_rocket',     false),
      feature_dashboard_courses:   cfg.get('feature_dashboard_courses',   false),
      feature_dashboard_available_student:     cfg.get('feature_dashboard_available_student',     false),
      feature_dashboard_available_supervisor:  cfg.get('feature_dashboard_available_supervisor',  false),
      feature_dashboard_available_courseadmin: cfg.get('feature_dashboard_available_courseadmin', false),
      feature_badges:              cfg.get('feature_badges',              false),
      feature_ai:                  cfg.get('feature_ai',                  false),
      feature_ai_step_gate:        cfg.get('feature_ai_step_gate',        false),
      feature_word_clarification:  cfg.get('feature_word_clarification',  false),
      feature_ai_course_wizard:    cfg.get('feature_ai_course_wizard',    false),
      feature_supervisor:          cfg.get('feature_supervisor',          false),
      feature_twin:                cfg.get('feature_twin',                false),
      feature_cross_company:       cfg.get('feature_cross_company',       false),
      feature_legacy_course_editor: cfg.get('feature_legacy_course_editor', false),
      feature_subtitle_editor:     cfg.get('feature_subtitle_editor',     false),
      feature_website_sales:       cfg.get('feature_website_sales',       false),
      feature_help:                cfg.get('feature_help',                true),
      feature_ihaveawin:           cfg.get('feature_ihaveawin',           false),
      feature_game:                cfg.get('feature_game',                false),
      feature_completion_publish:  cfg.get('feature_completion_publish',  true),
      feature_orientation_tour:    cfg.get('feature_orientation_tour',    false),
      feature_study_matrix:        cfg.get('feature_study_matrix',        false),
      feature_student_groups:      cfg.get('feature_student_groups',      false),
      feature_reports:             cfg.get('feature_reports',             false),
      feature_awards:              cfg.get('feature_awards',              false),
      feature_d5_rating:           cfg.get('feature_d5_rating',           false),
      feature_study_plans:         cfg.get('feature_study_plans',         false),
      // Checksheet / sidebar UI
      feature_checksheet_collapsed:    cfg.get('feature_checksheet_collapsed',    false),
      feature_checksheet_hidden:       cfg.get('feature_checksheet_hidden',       false),
      feature_checksheet_progress_bar: cfg.get('feature_checksheet_progress_bar', false),
      feature_checksheet_full_color:   cfg.get('feature_checksheet_full_color',   false),
      // Step-view presentation
      feature_thumbs_up_buttons:       cfg.get('feature_thumbs_up_buttons',       false),
      feature_show_step_messages:      cfg.get('feature_show_step_messages',      true),
      feature_simple_presentation:     cfg.get('feature_simple_presentation',     false),
      feature_show_step_number:        cfg.get('feature_show_step_number',        true),
      // Editor
      feature_hide_editor_toolbar:     cfg.get('feature_hide_editor_toolbar',     false),
      feature_voice_input:             cfg.get('feature_voice_input',             false),
      feature_input_selector_images:   cfg.get('feature_input_selector_images',   true),
    };
    const merged = { ...configDefaults, ...(sys || {}) } as Record<string, any>;
    for (const key of Object.keys(merged)) {
      if (isFeatureFlagKey(key) && !this.isLicensed(key)) {
        merged[key] = false;
      }
    }
    return merged as SystemSettings;
  }

  /** Get current company settings */
  getCompanySettings(): CompanySettings | null {
    return this._companySettings$.getValue();
  }

  /** The global e-commerce switch is on (System Settings, Feature Flags). */
  isWebsiteSalesAllowed(): boolean {
    return !!this.getSystemSettings().feature_website_sales;
  }

  /** Online sales are on for the current company: global switch and company Website Sales. */
  isWebsiteSalesEnabled(): boolean {
    return this.isWebsiteSalesAllowed() && !!this.getCompanySettings()?.website_sales?.enabled;
  }

  /**
   * Returns the inactivity timeout in minutes for the current user's company.
   * Returns 0 if auto-logout is disabled.
   * Falls back to 15 minutes if not configured.
   */
  getInactivityTimeoutMinutes(): number {
    const company = this._companySettings$.getValue();
    const configured = company?.session?.inactivity_timeout_minutes;
    if (configured === undefined || configured === null) {
      return 15;
    }
    return configured;
  }

  /**
   * True when the company requires students to explicitly click OK to acknowledge
   * supervisor messages (company_settings.messaging.ack_required).
   */
  get ackEnabled(): boolean {
    return !!(this._companySettings$.getValue()?.messaging?.ack_required);
  }

  // ═══════════════════════════════════════════════════════
  //  HTTP OPERATIONS (Admin UI)
  // ═══════════════════════════════════════════════════════

  /** GET /system-settings — fetch from server (admin only) */
  fetchSystemSettings(): Observable<SystemSettings> {
    console.log('[FeatureSettingsService] fetchSystemSettings GET...');
    return this.http
      .get<any>(`${this.baseUrl}/system-settings`, httpOptions)
      .pipe(
        tap((rawRes) => {
          console.log('[FeatureSettingsService] fetchSystemSettings RAW RESPONSE:', JSON.stringify(rawRes));
          console.log('[FeatureSettingsService] feature_firststart from server =', rawRes?.data?.feature_firststart);
        }),
        map((res) => res.data as SystemSettings),
        tap((settings) => {
          console.log('[FeatureSettingsService] fetchSystemSettings PARSED:', JSON.stringify(settings));
          this._systemSettings$.next(settings);
          this.saveToStorage(STORAGE_KEY_SYS, settings);
        }),
        catchError(this.handleError)
      );
  }

  /** POST /system-settings — save to server (admin only) */
  saveSystemSettings(settings: Partial<SystemSettings>): Observable<SystemSettings> {
    console.log('[FeatureSettingsService] saveSystemSettings SENDING:', JSON.stringify({ settings }));
    return this.http
      .post<any>(
        `${this.baseUrl}/system-settings`,
        JSON.stringify({ settings }),
        httpOptions
      )
      .pipe(
        tap((rawRes) => {
          console.log('[FeatureSettingsService] saveSystemSettings RAW RESPONSE:', JSON.stringify(rawRes));
          if (rawRes?._debug) {
            console.log('[FeatureSettingsService] === SERVER DEBUG ===');
            (rawRes._debug as string[]).forEach((d: string) => console.log('  ', d));
          }
        }),
        map((res) => res.data as SystemSettings),
        tap((saved) => {
          console.log('[FeatureSettingsService] saveSystemSettings PARSED:', JSON.stringify(saved));
          this._systemSettings$.next(saved);
          this.saveToStorage(STORAGE_KEY_SYS, saved);
        }),
        catchError(this.handleError)
      );
  }

  /** GET /company-settings/{id} — fetch company settings */
  fetchCompanySettings(companyId: number): Observable<CompanySettingsResponse> {
    return this.http
      .get<any>(`${this.baseUrl}/company-settings/${companyId}`, httpOptions)
      .pipe(
        map((res) => ({
          data: res.data as CompanySettings,
          editable_keys: res.editable_keys || [],
          system_features: res.system_features as SystemSettings,
          system_languages: (res.system_languages || []) as string[],
          language_status: res.language_status || {},
          company_id: Number(res.company_id),
        } as CompanySettingsResponse)),
        tap((result) => {
          this._companySettings$.next(result.data);
          this.saveToStorage(STORAGE_KEY_COMPANY, result.data);
        }),
        catchError(this.handleError)
      );
  }

  loadCompanySettings(companyId?: number): Observable<CompanySettings> {
    if (companyId && companyId > 0) {
      return this.fetchCompanySettings(companyId).pipe(map((res) => res.data));
    }
    return this.companySettings$;
  }

  /** POST /company-settings/{id} — save company settings */
  saveCompanySettings(companyId: number, settings: Partial<CompanySettings>): Observable<CompanySettings> {
    return this.http
      .post<any>(
        `${this.baseUrl}/company-settings/${companyId}`,
        JSON.stringify({ settings }),
        httpOptions
      )
      .pipe(
        map((res) => res.data as CompanySettings),
        tap((saved) => {
          this._companySettings$.next(saved);
          this.saveToStorage(STORAGE_KEY_COMPANY, saved);
          this.applyFaviconBranding(saved);
          this.applyCustomStepCss(saved);
        }),
        catchError(this.handleError)
      );
  }

  /**
   * POST /company-settings/{id}/website-sales/regenerate-key
   * Generates a fresh Website Sales API key. Returns the key once, in clear text.
   */
  regenerateWebsiteSalesKey(companyId: number): Observable<{ webhook_secret: string }> {
    return this.http
      .post<any>(`${this.baseUrl}/company-settings/${companyId}/website-sales/regenerate-key`, JSON.stringify({}), httpOptions)
      .pipe(
        map((res) => ({ webhook_secret: (res.data?.webhook_secret || '') as string })),
        catchError(this.handleError)
      );
  }

  /**
   * GET /company-settings/{id}/website-sales/key
   * Reveal the stored API key in clear text (for re-copy / comparison).
   */
  getWebsiteSalesKey(companyId: number): Observable<{ webhook_secret: string }> {
    return this.http
      .get<any>(`${this.baseUrl}/company-settings/${companyId}/website-sales/key`, httpOptions)
      .pipe(
        map((res) => ({ webhook_secret: (res.data?.webhook_secret || '') as string })),
        catchError(this.handleError)
      );
  }

  getImageStyle(companyId: number): Observable<{ style_prompt: string; reference_images: string[] }> {
    return this.http
      .get<any>(`${this.baseUrl}/company-settings/${companyId}/image-style`, httpOptions)
      .pipe(map((res) => res.data));
  }

  saveImageStyle(
    companyId: number,
    data: { style_prompt: string; reference_images: string[] }
  ): Observable<{ style_prompt: string; reference_images: string[] }> {
    return this.http
      .post<any>(`${this.baseUrl}/company-settings/${companyId}/image-style`, JSON.stringify(data), httpOptions)
      .pipe(map((res) => res.data));
  }

  testImageStyle(
    companyId: number,
    data: { prompt?: string; style_prompt?: string; reference_images?: string[] }
  ): Observable<any> {
    return this.http
      .post<any>(`${this.baseUrl}/company-settings/${companyId}/image-style/test`, JSON.stringify(data), httpOptions)
      .pipe(map((res) => res.data));
  }

  /** POST /company-settings/bulk-features — apply feature flags to ALL companies (SuperUser only) */
  bulkApplyFeaturesToAllCompanies(features: Partial<SystemSettings>): Observable<{ updated: number }> {
    return this.http
      .post<any>(`${this.baseUrl}/company-settings/bulk-features`, JSON.stringify({ features }), httpOptions)
      .pipe(
        map((res) => ({ updated: res.updated as number })),
        catchError(this.handleError)
      );
  }

  /** POST /company-settings/bulk-section — replace one settings section in ALL companies (SuperUser only) */
  bulkApplySectionToAllCompanies(section: string, value: Partial<CompanySettings>): Observable<{ updated: number }> {
    return this.http
      .post<any>(`${this.baseUrl}/company-settings/bulk-section`, JSON.stringify({ section, value }), httpOptions)
      .pipe(
        map((res) => ({ updated: Number(res.updated) })),
        catchError(this.handleError)
      );
  }

  /** GET /system-settings/staff-onboarding — onboarding for staff without a company (SuperUser/SysAdmin) */
  fetchStaffOnboarding(): Observable<StaffOnboardingResponse> {
    return this.http
      .get<any>(`${this.baseUrl}/system-settings/staff-onboarding`, httpOptions)
      .pipe(
        map((res) => ({
          data: res.data as StaffOnboardingSettings,
          pages: (res.pages || {}) as Record<string, string[]>,
          system_languages: (res.system_languages || []) as string[],
        })),
        catchError(this.handleError)
      );
  }

  /** GET /system-settings/branding — system login page and email branding (SuperUser/SysAdmin) */
  fetchSystemBranding(): Observable<SystemBrandingSettings> {
    return this.http
      .get<any>(`${this.baseUrl}/system-settings/branding`, httpOptions)
      .pipe(
        map((res) => res.data as SystemBrandingSettings),
        catchError(this.handleError)
      );
  }

  /** POST /system-settings/branding */
  saveSystemBranding(settings: SystemBrandingSettings): Observable<SystemBrandingSettings> {
    return this.http
      .post<any>(`${this.baseUrl}/system-settings/branding`, JSON.stringify({ settings }), httpOptions)
      .pipe(
        map((res) => res.data as SystemBrandingSettings),
        catchError(this.handleError)
      );
  }

  /** POST /system-settings/staff-onboarding — sections sent replace the stored ones */
  saveStaffOnboarding(settings: Partial<StaffOnboardingSettings>): Observable<StaffOnboardingSettings> {
    return this.http
      .post<any>(`${this.baseUrl}/system-settings/staff-onboarding`, JSON.stringify({ settings }), httpOptions)
      .pipe(
        map((res) => res.data as StaffOnboardingSettings),
        catchError(this.handleError)
      );
  }

  // ─── Helpers ──────────────────────────────────────────

  private handleError(error: any): Observable<never> {
    console.error('[FeatureSettingsService]', error);
    return throwError(() => error);
  }
}

